import { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from '@turf/turf';
import GeoData, { IGeoDataProps } from 'DesignSystem/Inputs/GeoData/GeoData';
import { FieldProps, useField, useFormikContext } from 'formik';
import React, { FC } from 'react';

export type IGeoDataFieldProps = {
  areaFieldName?: string;
} & Omit<IGeoDataProps, 'value' | 'onChange'> &
  (
    | ({
        /**
         * Defines if the component is showing a feature collection or a single feature (point or polygon)
         * 'feature' will only allow adding a single point or polygon
         * @default 'feature-collection'
         */
        type?: 'feature-collection';
      } & FieldProps<FeatureCollection<Polygon | Point | MultiPolygon>>)
    | ({
        type?: 'feature';
      } & FieldProps<Feature<Polygon | Point | MultiPolygon> | null>)
  );

/**
 * Pass optional areaFieldName to automatically update area size field in hectares when geo data changes
 */
const GeoDataField: FC<IGeoDataFieldProps> = ({
  field: { value, name },
  areaFieldName,
  type = 'feature-collection',
  ...props
}) => {
  const { setFieldValue } = useFormikContext();
  const [, , { setValue }] = useField(name);

  const handleFeatureCollectionChange = (
    newValue: FeatureCollection<Polygon | Point | MultiPolygon>,
    areaSizeHa: number
  ) => {
    setValue(newValue);
    if (areaFieldName && areaSizeHa !== undefined) {
      setFieldValue(areaFieldName, areaSizeHa);
    }
  };

  const handleFeatureChange = (
    newValue: Feature<Polygon | Point | MultiPolygon> | null,
    areaSizeHa: number
  ) => {
    setValue(newValue);
    if (areaFieldName && areaSizeHa) {
      setFieldValue(areaFieldName, areaSizeHa);
    }
  };

  if (type === 'feature-collection') {
    return (
      <GeoData
        value={value as FeatureCollection<Polygon | Point | MultiPolygon>}
        type="feature-collection"
        onChange={handleFeatureCollectionChange}
        {...props}
      />
    );
  }

  return (
    <GeoData
      value={value as Feature<Polygon | Point | MultiPolygon> | null}
      type="feature"
      onChange={handleFeatureChange}
      {...props}
    />
  );
};

export default GeoDataField;
